import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './TimesheetExceptionsPage.css';
import {
  ShieldAlert, Download, RefreshCw, AlertTriangle, CheckCircle2, Pause, Settings, Mail,
  Search, History, Unlock, UserX, MailCheck, MailX, MailMinus, EyeOff, ChevronsUp,
  CornerDownRight, MessageSquare, Clock, Check, Undo2, Pencil, X, StickyNote, ChevronRight, ChevronDown,
} from 'lucide-react';
import FilterBar from '../../components/FilterBar';
import Highlight from '../../components/Highlight';
import { useSiteContext } from '../../SiteContext';
import { usePayPeriods } from '../../hooks/usePayPeriods';
import { useSelectPayPeriod } from '../../hooks/useSelectPayPeriod';
import { consumeStashedDeepLink } from '../../interfaces/DeepLinkInterface';
import {
  TimesheetExceptionsPayload, ExceptionEvent, ExceptionItem, SubmissionState, ReviewRequestAction,
} from '../../interfaces/TimesheetExceptions';
import {
  getTimesheetExceptions, getTimesheetExceptionsErrorMessage,
  downloadExceptionsExcel, exportExceptionsErrorMessage,
  setTimesheetExceptionReview, setTimesheetExceptionReviewErrorMessage, reviewErrorStatus,
} from '../../api/timesheetExceptions';
import {
  ReviewFilter, reviewSupported, matchesReviewFilter, eventsInReviewScope, recountReview,
  applyReviewResult, filterEmptyState, historyActionLabel, formatReviewedAt, reviewTooltip,
} from './timesheetExceptionsReview';

/**
 * Timesheet Exceptions — answers and comments from this period's submissions that
 * matched a configured trigger. Replaces the v1 Timesheet Comments page; a comment
 * is now one kind of exception.
 *
 * The answers are a read-only snapshot. The one write is the optional review mark
 * (who followed a submission up, when, and a note — with history); nothing is
 * required or blocked on it, and the page opens on All. Pay-period scoped, reading the
 * shell's global week pill — this page renders no picker of its own. (GpsExceptions
 * and SelfieExceptions both render a second one under the shell's; don't copy that.)
 */

const APPROVAL_LABELS: Record<number, string> = {
  1: 'Supervisor approval pending',
  2: 'Payroll approval pending',
  3: 'Approved',
  4: 'Manager approval pending',
};

const ANSWER_TYPE_LABELS: Record<number, string> = {
  1: 'Yes/No',
  2: 'Date',
  3: 'Checkbox',
  4: 'Meal waiver',
  5: 'Comments',
};

type ItemKind = 'all' | 'comments' | 'answers';

const nameOf = (n: { first: string; middle: string | null; last: string } | null): string =>
  (n ? [n.first, n.middle, n.last].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim() : '');

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

/** Deterministic avatar hue, matching the other v2 exception pages. */
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `hsl(${h} 46% 46%)`;
}

/** "-07:00" / "+05:30" → minutes from UTC. Null for anything unparseable. */
function offsetMinutes(offset: string | null): number | null {
  if (!offset) return null;
  const m = /^([+-])(\d{2}):?(\d{2})$/.exec(offset.trim());
  if (!m) return null;
  const mins = Number(m[2]) * 60 + Number(m[3]);
  return m[1] === '-' ? -mins : mins;
}

/**
 * Submit time in the EMPLOYEE's local time, matching the alert email.
 *
 * SubmittedDate is stored as an instant (UTC); SubmittedDateOffset carries the
 * offset that was in force where the employee submitted. So the local wall clock
 * is the instant plus that offset, then formatted as UTC to stop the viewer's own
 * timezone shifting it a second time.
 *
 * Without the offset this renders in whoever-is-looking's timezone, which is how
 * the same submission ends up showing two different times to two people.
 */
function submittedLabel(e: ExceptionEvent): string {
  const utcMs = Date.parse(e.submittedDate);
  if (Number.isNaN(utcMs)) return String(e.submittedDate);

  const mins = offsetMinutes(e.submittedDateOffset);
  const local = new Date(utcMs + (mins ?? 0) * 60000);

  return local.toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  }).replace(',', ' ·');
}

function formatRange(startStr: string, endStr: string): string {
  if (!startStr || !endStr) return '';
  const s = new Date(`${startStr}T00:00:00Z`);
  const en = new Date(`${endStr}T00:00:00Z`);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString(undefined, opts)} – ${en.toLocaleDateString(undefined, { ...opts, year: 'numeric' })}`;
}

/**
 * The caption under each question — the same shape the email uses. `matchRule` is
 * never rendered; it is an internal value. The one case that carries user meaning
 * is the meal waiver, said in words instead.
 */
function captionFor(item: ExceptionItem): string {
  const type = ANSWER_TYPE_LABELS[item.answerType] ?? 'Answer';
  if (item.itemRole === 'Context') return `Follow-up · ${type}`;
  const kind = item.exceptionType === 'Comment' ? 'Comment' : 'Answer';
  // "fires on", never "expected" — triggerAnswer is the value that RAISES the
  // exception, so on 20/20's "I confirm I did not sustain an injury" (which fires
  // on No) the expected answer is actually Yes. Saying "expected No" beside an
  // answer of No reads as though No were correct.
  if (item.triggerAnswer == null) return `${kind} · ${type} · fires on any answer`;
  if (item.matchRule === 'PrefixCI' || item.answerType === 4) {
    return `${kind} · ${type} · fires on ${item.triggerAnswer} — with or without dates`;
  }
  return `${kind} · ${type} · fires on ${item.triggerAnswer}`;
}

// ── Review popup ─────────────────────────────────────────────────────────────
// Copied from GpsExceptionsPage's MarkReviewedPopup (GPS stays untouched — its CSS
// is .gpx-scoped and the popup is typed on GPS rows). One popup serves both
// "Mark reviewed" and "Edit note"; the note is optional either way.

type ReviewPopupMode = 'review' | 'editNote';

function ReviewPopup({
  event, mode, onClose, onSubmit,
}: {
  event: ExceptionEvent;
  mode: ReviewPopupMode;
  onClose: () => void;
  /** Round-trips the PATCH; rejects on failure so the popup can show why. */
  onSubmit: (id: number, action: ReviewRequestAction, note: string | null) => Promise<void>;
}) {
  const [note, setNote] = useState(mode === 'editNote' ? (event.reviewNote ?? '') : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => { taRef.current?.focus(); }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      // Empty textarea → note:null, identical to a note-less mark.
      await onSubmit(event.timesheetExceptionEventID, mode, note.trim() || null);
      onClose();
    } catch (e) {
      // Keep the typed note; just surface the error and re-enable the button.
      setError(setTimesheetExceptionReviewErrorMessage(e));
      setSaving(false);
    }
  };

  const employee = nameOf(event.employeeName);

  return (
    <div className="tex-quickpop-bg" onClick={onClose}>
      <div className="tex-quickpop" onClick={(e) => e.stopPropagation()}>
        <div className="qp-head">
          <div className="qp-ic">{mode === 'editNote' ? <Pencil /> : <CheckCircle2 />}</div>
          <div>
            <h3>{mode === 'editNote' ? 'Edit review note' : 'Mark reviewed'}</h3>
            <div className="qp-sub">
              <span>{employee}</span>
              <span>·</span>
              <span className="mono">{event.altEmployeeID ?? '—'}</span>
              <span>·</span>
              <span className="mono">{submittedLabel(event)}</span>
            </div>
          </div>
          <button type="button" className="x" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="qp-body">
          <label className="qp-lab" htmlFor="tex-qp-note">Note (optional)</label>
          <textarea
            id="tex-qp-note"
            ref={taRef}
            className="qp-ta"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a note (optional) — e.g. spoke with the employee, forwarded to safety, no action needed…"
            maxLength={1000}
            disabled={saving}
          />
          {error && <div className="qp-err"><AlertTriangle />{`Couldn't save: ${error}`}</div>}
        </div>
        <div className="qp-foot">
          <button type="button" className="btn qp-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn rev-mark" onClick={submit} disabled={saving}>
            <Check />{saving ? 'Saving…' : mode === 'editNote' ? 'Save note' : 'Mark reviewed'}
          </button>
        </div>
      </div>
    </div>
  );
}

const TimesheetExceptionsPage: React.FC = () => {
  const { payPeriodID, userRole, createPopup, pendingDeepLink } = useSiteContext();
  const { payPeriods } = usePayPeriods();
  const selectPayPeriod = useSelectPayPeriod();

  const [data, setData] = useState<TimesheetExceptionsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<ItemKind>('all');
  /** Replaced submissions are shown by default; the toggle hides them. */
  const [hideReplaced, setHideReplaced] = useState(false);
  const [esOpen, setEsOpen] = useState<Set<number>>(new Set());
  const [errorOpen, setErrorOpen] = useState<Set<number>>(new Set());
  const [exporting, setExporting] = useState(false);

  /**
   * Optional review segment. Defaults to All so the page opens on today's view —
   * reviews are informational, never a queue someone has to clear.
   */
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  // Keyed by event id: which cards' inline Undo is mid-PATCH / last errored.
  // `retry` is false for a 409 (state changed elsewhere; a retry can't succeed).
  const [reviewPending, setReviewPending] = useState<Record<number, boolean>>({});
  const [reviewCardError, setReviewCardError] = useState<Record<number, { message: string; retry: boolean }>>({});
  /** Which card's review popup is open, and whether it marks or edits the note. */
  const [reviewPopup, setReviewPopup] = useState<{ eventID: number; mode: ReviewPopupMode } | null>(null);
  /** Which card is showing the inline "Undo review? · Yes / Cancel" confirm. */
  const [confirmUndoID, setConfirmUndoID] = useState<number | null>(null);
  const [historyOpen, setHistoryOpen] = useState<Set<number>>(new Set());
  /** Set by a 503 — the review DDL isn't on this database yet; controls hide. */
  const [reviewUnavailable, setReviewUnavailable] = useState<string | null>(null);

  /** Deep-link arrival from the alert email. */
  const [arrivedEventId, setArrivedEventId] = useState<number | null>(null);
  const [arrivalDismissed, setArrivalDismissed] = useState(false);
  const [arrivalMissing, setArrivalMissing] = useState(false);
  const deepLinkConsumed = useRef(false);
  const arrivalScrolled = useRef(false);

  const load = useCallback(async (showSpinner: boolean) => {
    if (payPeriodID == null) return;
    if (showSpinner) setLoading(true);
    setLoadError(null);
    try {
      const payload = await getTimesheetExceptions(payPeriodID);
      setData(payload);
      setUpdatedAt(new Date());
    } catch (e) {
      setLoadError(getTimesheetExceptionsErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [payPeriodID]);

  useEffect(() => { load(true); }, [load]);

  /**
   * Deep-link arrival from the alert email (?page=timesheetExceptions&payPeriodID=&event=).
   *
   * Read from SiteContext first, falling back to the module-level stash: useAdminSession
   * clears pendingDeepLink in the same effect that sets the page, and React batches
   * those, so this page can mount after the context value is already null.
   *
   * The link's pay period is almost never the one currently selected — an alert read
   * days later points at a past week — so the period is selected before anything else.
   */
  useEffect(() => {
    if (deepLinkConsumed.current) return;
    const link = pendingDeepLink?.eventId ? pendingDeepLink : consumeStashedDeepLink();
    if (!link?.eventId) return;

    if (link.payPeriodID && link.payPeriodID !== payPeriodID) {
      const target = payPeriods.find((pp) => pp.PayPeriodID === link.payPeriodID);
      if (target) {
        deepLinkConsumed.current = true;
        setArrivedEventId(link.eventId);
        // A reviewed or replaced target must still show.
        setReviewFilter('all');
        setHideReplaced(false);
        selectPayPeriod(target);
        return;
      }
      // Pay periods haven't loaded yet — let a later pass try again rather than
      // landing on the wrong week.
      if (payPeriods.length === 0) return;
    }

    deepLinkConsumed.current = true;
    setArrivedEventId(link.eventId);
    setReviewFilter('all');
    setHideReplaced(false);
  }, [pendingDeepLink, payPeriods, payPeriodID, selectPayPeriod]);

  /** Once the target week's data is in, scroll to the event and flash it. */
  useEffect(() => {
    if (arrivedEventId == null || loading || !data || arrivalScrolled.current) return;

    const present = data.events.some((e) => e.timesheetExceptionEventID === arrivedEventId);
    if (!present) {
      // The row may be filtered out, in another period, or outside this caller's
      // scope. Say so rather than silently doing nothing.
      setArrivalMissing(true);
      arrivalScrolled.current = true;
      return;
    }

    arrivalScrolled.current = true;
    window.setTimeout(() => {
      const el = document.getElementById(`tex-ev-${arrivedEventId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.classList.add('flash');
      window.setTimeout(() => el?.classList.remove('flash'), 1800);
    }, 120);
  }, [arrivedEventId, loading, data]);

  const toggle = (set: Set<number>, apply: (s: Set<number>) => void) => (id: number) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    apply(next);
  };
  const toggleEs = toggle(esOpen, setEsOpen);
  const toggleError = toggle(errorOpen, setErrorOpen);
  const toggleHistory = toggle(historyOpen, setHistoryOpen);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const term = query.trim().toLowerCase();

  /** The review segment + "Hide replaced" applied — the base the kind/search filters narrow. */
  const scopedEvents = useMemo(
    () => (data ? eventsInReviewScope(data.events, reviewFilter, hideReplaced) : []),
    [data, reviewFilter, hideReplaced],
  );

  const visibleEvents = useMemo(() => {
    return scopedEvents
      .map((e) => {
        // Filter items, then drop a context row whose trigger was filtered away —
        // a follow-up with nothing to follow reads as an orphan.
        const kept = e.items.filter((i) => {
          if (kind === 'comments' && i.exceptionType !== 'Comment') return false;
          if (kind === 'answers' && i.exceptionType !== 'Answer') return false;
          return true;
        });
        const keptTriggerIds = new Set(
          kept.filter((i) => i.itemRole !== 'Context').map((i) => i.timesheetQuestionsID),
        );
        const items = kept.filter(
          (i) => i.itemRole !== 'Context'
            || (i.parentTimesheetQuestionsID != null && keptTriggerIds.has(i.parentTimesheetQuestionsID)),
        );
        return { ...e, items };
      })
      .filter((e) => e.items.length > 0)
      .filter((e) => {
        if (!term) return true;
        const haystack = [
          nameOf(e.employeeName), e.altEmployeeID ?? '', nameOf(e.supervisorName),
          ...e.items.flatMap((i) => [i.questionText, i.answerText, i.questionTextEs ?? '']),
        ].join(' ').toLowerCase();
        return haystack.includes(term);
      });
  }, [scopedEvents, kind, term]);

  const filtering = term.length > 0 || kind !== 'all';
  // "Hide replaced" is a no-op under Unreviewed (replaced submissions are already
  // left out there), so it neither hides anything nor gets reported as hiding.
  const hideApplies = hideReplaced && reviewFilter !== 'unreviewed';
  const hiddenReplaced = data && hideApplies
    ? data.events.filter((e) => e.submissionState === 'Superseded' && matchesReviewFilter(e, reviewFilter)).length
    : 0;
  /** "Y" in "X of Y" — everything in the active review segment, before kind/search. */
  const shownTotal = scopedEvents.length;

  const reviewsOn = !!data && reviewSupported(data.events);
  /** Card controls hide on a 503 (DDL not applied); the segment stays, it's harmless. */
  const reviewControlsOn = reviewsOn && reviewUnavailable == null;
  const reviewCounts = useMemo(
    () => (data ? recountReview(data.counts, data.events) : null),
    [data],
  );

  const visibleItemCount = visibleEvents.reduce(
    (n, e) => n + e.items.filter((i) => i.itemRole !== 'Context').length, 0,
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  const doExport = async () => {
    if (!data || exporting) return;
    setExporting(true);
    try {
      await downloadExceptionsExcel(data.payPeriod.payPeriodID, data.payPeriod.weekNumber);
    } catch (e) {
      createPopup('Couldn\'t build the export', exportExceptionsErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  const goToEvent = (eventID: number) => {
    setReviewFilter('all');
    setHideReplaced(false);
    setQuery('');
    setKind('all');
    window.setTimeout(() => {
      const el = document.getElementById(`tex-ev-${eventID}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el?.classList.add('flash');
      window.setTimeout(() => el?.classList.remove('flash'), 1600);
    }, 60);
  };

  /**
   * One review PATCH, applied in place. A 409 means someone else changed it first:
   * the server's message is shown and the list refetched underneath. A 503 means
   * the review DDL isn't on this database yet: controls hide and the message shows.
   * Rethrows so the caller (popup or inline Undo) can show the error where it was.
   */
  const runReview = useCallback(async (id: number, action: ReviewRequestAction, note?: string | null) => {
    try {
      const result = await setTimesheetExceptionReview(id, action, note);
      setData((prev) => (prev ? applyReviewResult(prev, result) : prev));
      setReviewCardError((p) => { const n = { ...p }; delete n[id]; return n; });
    } catch (e) {
      const status = reviewErrorStatus(e);
      if (status === 503) setReviewUnavailable(setTimesheetExceptionReviewErrorMessage(e));
      if (status === 409) load(false);
      throw e;
    }
  }, [load]);

  /** Inline Undo (after its Yes/Cancel confirm); tracks per-card pending/error. */
  const clearReview = async (id: number) => {
    setReviewPending((p) => ({ ...p, [id]: true }));
    setReviewCardError((p) => { const n = { ...p }; delete n[id]; return n; });
    try {
      await runReview(id, 'clear');
    } catch (e) {
      const status = reviewErrorStatus(e);
      // 503 is shown page-wide by runReview.
      if (status !== 503) {
        setReviewCardError((p) => ({
          ...p, [id]: { message: setTimesheetExceptionReviewErrorMessage(e), retry: status !== 409 },
        }));
      }
    } finally {
      setReviewPending((p) => { const n = { ...p }; delete n[id]; return n; });
    }
  };

  // ── Render pieces ───────────────────────────────────────────────────────────

  /**
   * Card-header review cell: pill (or dash) + 30×30 actions, laid out on a fixed
   * grid so the buttons line up down the list. Pattern from GPS's review column.
   */
  const reviewCell = (e: ExceptionEvent) => {
    const id = e.timesheetExceptionEventID;
    const pending = !!reviewPending[id];
    const cardErr = reviewCardError[id];

    if (confirmUndoID === id) {
      return (
        <div className="rev-confirm">
          <span className="rc-txt">Undo review?</span>
          <button type="button" className="rc-yes" onClick={() => { setConfirmUndoID(null); clearReview(id); }}>
            Yes
          </button>
          <button type="button" className="rc-cancel" onClick={() => setConfirmUndoID(null)}>Cancel</button>
        </div>
      );
    }

    return (
      <div className="rev-cell">
        {e.reviewed ? (
          <span className="revpill yes" title={reviewTooltip(e)}>
            Reviewed
            {e.reviewNote && <StickyNote />}
          </span>
        ) : (
          <span className="revpill no mono">—</span>
        )}
        {reviewControlsOn && (e.reviewed ? (
          <>
            <button
              type="button"
              className="rev-tgl edit"
              title="Edit note…"
              disabled={pending}
              onClick={() => setReviewPopup({ eventID: id, mode: 'editNote' })}
            >
              <Pencil />
            </button>
            <button
              type="button"
              className="rev-tgl undo"
              title="Undo review"
              disabled={pending}
              onClick={() => setConfirmUndoID(id)}
            >
              {pending ? <RefreshCw className="spin" /> : <Undo2 />}
            </button>
          </>
        ) : (
          <button
            type="button"
            className="rev-tgl mark"
            title="Mark reviewed…"
            disabled={pending}
            onClick={() => setReviewPopup({ eventID: id, mode: 'review' })}
          >
            {pending ? <RefreshCw className="spin" /> : <Check />}
          </button>
        ))}
        {cardErr && (cardErr.retry ? (
          <button type="button" className="rev-err" title={cardErr.message} onClick={() => clearReview(id)}>
            Couldn&apos;t save · Retry
          </button>
        ) : (
          <span className="rev-err static">{cardErr.message}</span>
        ))}
      </div>
    );
  };

  /**
   * Card-body review rows: who reviewed it and the note, a collapsible history
   * once there's more than one entry, and the replaced submission's review when
   * this is a resubmission.
   */
  const reviewRows = (e: ExceptionEvent) => {
    const history = e.reviewHistory ?? [];
    const showHistory = history.length > 1;
    const open = historyOpen.has(e.timesheetExceptionEventID);
    const prev = e.previousSubmissionReview;

    return (
      <>
        {(e.reviewed || showHistory) && (
          <div className={`ev-note review${e.reviewed ? '' : ' cleared'}`}>
            {e.reviewed ? <CheckCircle2 size={13} /> : <History size={13} />}
            <div className="rv-main">
              {e.reviewed ? (
                <div>
                  <b>Reviewed by {e.reviewedByName ?? 'Unknown'}</b>
                  {e.reviewedAt && <span className="rv-at"> · {formatReviewedAt(e.reviewedAt)}</span>}
                </div>
              ) : (
                <div><b>Not reviewed</b> <span className="rv-at">— an earlier review was undone.</span></div>
              )}
              {e.reviewed && e.reviewNote && <div className="rv-note">{e.reviewNote}</div>}
              {open && (
                <ol className="rv-hist">
                  {history.map((h) => (
                    <li key={h.timesheetExceptionReviewLogID}>
                      <span className={`rv-act ${h.action.toLowerCase()}`}>{historyActionLabel(h.action)}</span>
                      <span className="rv-who">{h.byName ?? 'Unknown'}</span>
                      <span className="rv-at">{formatReviewedAt(h.at)}</span>
                      {h.note && <span className="rv-hnote">{h.note}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>
            {showHistory && (
              <button type="button" className="ev-jump" onClick={() => toggleHistory(e.timesheetExceptionEventID)}>
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}History ({history.length})
              </button>
            )}
          </div>
        )}

        {prev && (
          <div className="ev-note prevrev">
            <History size={13} />
            <div className="rv-main">
              Previous submission reviewed by <b>{prev.reviewedByName ?? 'Unknown'}</b>
              {prev.reviewedAt && <span className="rv-at"> · {formatReviewedAt(prev.reviewedAt)}</span>}
              {prev.reviewNote && <span className="rv-prevnote"> · {prev.reviewNote}</span>}
            </div>
            <button type="button" className="ev-jump" onClick={() => goToEvent(prev.timesheetExceptionEventID)}>
              Go to it
            </button>
          </div>
        )}
      </>
    );
  };

  const stateChip = (e: ExceptionEvent) => {
    const map: Record<SubmissionState, { cls: string; icon: React.ReactNode; label: string }> = {
      Current: { cls: 'current', icon: <CheckCircle2 size={12} />, label: 'Current' },
      Superseded: { cls: 'superseded', icon: <History size={12} />, label: 'Superseded' },
      Withdrawn: { cls: 'withdrawn', icon: <Unlock size={12} />, label: 'Withdrawn' },
    };
    const spec = map[e.submissionState];
    return (
      <div className="tex-state">
        <span className={`stchip ${spec.cls}`}>{spec.icon}{spec.label}</span>
        {e.submissionState === 'Current' && e.approvalStatus != null && (
          <span className={`apprline${e.approvalStatus === 3 ? ' ok' : ''}`}>
            {APPROVAL_LABELS[e.approvalStatus] ?? `Status ${e.approvalStatus}`}
          </span>
        )}
      </div>
    );
  };

  const mailPill = (e: ExceptionEvent) => {
    const spec: Record<string, { cls: string; icon: React.ReactNode; sub: string }> = {
      Sent: { cls: 'sent', icon: <MailCheck size={12} />, sub: e.emailSentDate ? new Date(e.emailSentDate).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '' },
      Pending: { cls: 'pending', icon: <Clock size={12} />, sub: 'never sent' },
      Failed: { cls: 'failed', icon: <MailX size={12} />, sub: 'send failed' },
      Skipped: { cls: 'skipped', icon: <MailMinus size={12} />, sub: 'no recipients' },
      Suppressed: { cls: 'suppressed', icon: <EyeOff size={12} />, sub: 'test mode' },
    };
    const s = spec[e.emailStatus] ?? spec.Skipped;
    const clickable = e.emailStatus === 'Failed' && !!e.emailError;
    return (
      <div className="tex-mail">
        <button
          type="button"
          className={`empill ${s.cls}${clickable ? ' clickable' : ''}`}
          onClick={clickable ? () => toggleError(e.timesheetExceptionEventID) : undefined}
          disabled={!clickable}
        >
          {s.icon}{e.emailStatus}
          {e.emailAttemptCount > 1 && <span className="emx">sent {e.emailAttemptCount}×</span>}
        </button>
        {s.sub && <span className="emsub">{s.sub}</span>}
      </div>
    );
  };

  const itemRow = (item: ExceptionItem) => {
    const isContext = item.itemRole === 'Context';
    const isComment = item.exceptionType === 'Comment';
    return (
      <div className={`tex-item${isContext ? ' context' : ''}`} key={item.timesheetExceptionItemID}>
        <div className="i-kind">
          <span className={`ichip ${isContext ? 'context' : isComment ? 'comment' : 'answer'}`}>
            {isContext ? <CornerDownRight size={11} /> : isComment ? <MessageSquare size={11} /> : <AlertTriangle size={11} />}
            {isContext ? 'Follow-up' : item.exceptionType}
          </span>
        </div>
        <div className="i-q">
          <div className="i-qtext"><Highlight text={item.questionText} term={term} /></div>
          <div className="imeta">{captionFor(item)}</div>
          {esOpen.has(item.timesheetExceptionItemID) && item.questionTextEs && (
            <div className="i-es">{item.questionTextEs}</div>
          )}
          {item.questionTextEs && (
            <button type="button" className="i-esbtn" onClick={() => toggleEs(item.timesheetExceptionItemID)}>ES</button>
          )}
        </div>
        <div className="i-a">
          {isComment ? (
            <div className="i-prose"><Highlight text={item.answerText} term={term} /></div>
          ) : (
            <span className={`i-val${/^y/i.test(item.answerText) || /^s[íi]/i.test(item.answerText) ? ' yes' : ''}`}>
              <Highlight text={item.answerText} term={term} />
            </span>
          )}
        </div>
      </div>
    );
  };

  const eventCard = (e: ExceptionEvent) => {
    const employee = nameOf(e.employeeName);
    const triggerCount = e.items.filter((i) => i.itemRole !== 'Context').length;
    const contextCount = e.items.filter((i) => i.itemRole === 'Context').length;

    return (
      <div
        id={`tex-ev-${e.timesheetExceptionEventID}`}
        key={e.timesheetExceptionEventID}
        className={`tex-evt ${e.submissionState.toLowerCase()}${e.timesheetExceptionEventID === arrivedEventId ? ' arrived' : ''}`}
      >
        <div className={`ev-head${reviewsOn ? ' with-review' : ''}`}>
          <div className="ev-emp">
            <span className="ev-av" style={{ background: avatarColor(employee) }}>{initials(employee)}</span>
            <div className="ev-empmain">
              <div className="ev-name">
                <Highlight text={employee} term={term} />
                {!e.employeeActive && <span className="ev-inactive"><UserX size={11} />Inactive</span>}
                {(triggerCount > 1 || contextCount > 0) && (
                  <span className="ev-count">
                    {triggerCount} exception{triggerCount === 1 ? '' : 's'}
                    {contextCount > 0 && <span className="ev-ctx"> · +{contextCount} follow-up</span>}
                  </span>
                )}
              </div>
              <div className="ev-alt"><Highlight text={e.altEmployeeID ?? '—'} term={term} /></div>
            </div>
          </div>
          <div className="ev-sup">{nameOf(e.supervisorName) || <span className="dash">—</span>}</div>
          <div className="ev-sub">{submittedLabel(e)}</div>
          {stateChip(e)}
          {mailPill(e)}
          {reviewsOn && reviewCell(e)}
        </div>

        {e.submissionState === 'Superseded' && (
          <div className="ev-note superseded">
            Replaced by a newer submission from the same employee for this week. History only — don&apos;t act on it.
            {e.supersededByEventID != null && (
              <button type="button" className="ev-jump" onClick={() => goToEvent(e.supersededByEventID as number)}>
                Go to the current submission
              </button>
            )}
          </div>
        )}

        {e.submissionState === 'Withdrawn' && e.supersededByEventID == null && (
          <div className="ev-note withdrawn">
            <b>Unlocked and never resubmitted.</b> {employee} has no submitted timesheet for this week — what they
            reported here is the only record of it.
          </div>
        )}

        {/* Withdrawn with a later submission: that later one was unlocked too (no live
            timesheet exists, or this would be Superseded), so it's the one to look at. */}
        {e.submissionState === 'Withdrawn' && e.supersededByEventID != null && (
          <div className="ev-note superseded">
            Unlocked, then resubmitted — and the later submission was unlocked as well. The later one is the latest
            record for this week.
            <button type="button" className="ev-jump" onClick={() => goToEvent(e.supersededByEventID as number)}>
              Go to the later submission
            </button>
          </div>
        )}

        {reviewsOn && reviewRows(e)}

        {errorOpen.has(e.timesheetExceptionEventID) && e.emailError && (
          <div className="ev-note failed">
            <b>Send failed on attempt {e.emailAttemptCount}.</b>
            <span className="ev-raw">{e.emailError}</span>
          </div>
        )}

        {(e.chainNotified.length > 0 || e.chainWarning) && (
          <div className="ev-note chain">
            <ChevronsUp size={13} />
            <div>
              {e.chainNotified.length > 0 ? (
                <>
                  <b>Supervisor chain CC&apos;d:</b>{' '}
                  {e.chainNotified.map((m, i) => (
                    <span className="chain-m" key={m.employeeID}>
                      {m.name}{m.role && <span className="chain-r">{m.role}</span>}
                      {i < e.chainNotified.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </>
              ) : (
                <b>Supervisor chain: nobody notified.</b>
              )}
              {e.chainWarning && <div className="chain-w">{e.chainWarning}</div>}
            </div>
          </div>
        )}

        <div className="ev-items">{e.items.map(itemRow)}</div>
      </div>
    );
  };

  // ── Gates ───────────────────────────────────────────────────────────────────

  if (loading && !data) {
    return <div className="tex"><div className="tex-loading">Loading timesheet exceptions…</div></div>;
  }
  if (loadError && !data) {
    return (
      <div className="tex">
        <div className="tex-empty err">
          <span className="ei"><AlertTriangle size={20} /></span>
          <div><span className="e-title">Couldn&apos;t load timesheet exceptions.</span><br />{loadError}</div>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const { counts, payPeriod } = data;
  const periodLabel = formatRange(payPeriod.startDate, payPeriod.endDate);

  /** The five page states, in the order they take precedence. */
  const emptyState = (() => {
    if (data.events.length > 0) return null;
    if (!data.reportConfigured) {
      return (
        <div className="tex-empty">
          <span className="ei neutral"><Settings size={20} /></span>
          <div>
            <span className="e-title">Timesheet exception alerts aren&apos;t set up yet.</span><br />
            {userRole?.admin
              ? 'Choose the questions whose answers should raise an exception in Report Configuration.'
              : 'Ask an administrator to set this up in Report Configuration.'}
          </div>
        </div>
      );
    }
    if (data.questionCount === 0) {
      // The most dangerous state: it looks exactly like a clean week.
      return (
        <div className="tex-empty danger">
          <span className="ei danger"><AlertTriangle size={20} /></span>
          <div>
            <span className="e-title">The alert is active but no trigger questions are selected,</span> so nothing
            will ever fire.<br />
            This looks like a clean week. It isn&apos;t — nobody is watching.
          </div>
        </div>
      );
    }
    if (!data.reportActive) {
      return (
        <div className="tex-empty warn">
          <span className="ei warn"><Pause size={20} /></span>
          <div>
            <span className="e-title">Alert emails are paused.</span><br />
            <b>Exceptions are still being recorded</b> and will keep appearing on this page — only the email is
            stopped. Nothing is broken and nothing is lost.
          </div>
        </div>
      );
    }
    return (
      <div className="tex-empty ok">
        <span className="ei ok"><CheckCircle2 size={20} /></span>
        <div>
          <span className="e-title">Every timesheet submitted for {periodLabel} came back clean.</span><br />
          Alerts are on and {data.questionCount} question{data.questionCount === 1 ? ' is' : 's are'} being watched.
        </div>
      </div>
    );
  })();

  const firstUnsent = data.events.find((e) => e.emailStatus === 'Pending' || e.emailStatus === 'Failed');
  const firstWithdrawn = data.events.find((e) => e.submissionState === 'Withdrawn');
  const unsentCount = counts.emailFailed + counts.emailPending;
  const filterEmpty = filterEmptyState(data.events.length, visibleEvents.length, reviewFilter, shownTotal);
  const popupEvent = reviewPopup && reviewControlsOn
    ? data.events.find((e) => e.timesheetExceptionEventID === reviewPopup.eventID) ?? null
    : null;

  return (
    <div className="tex">
      <div className="page-head">
        <span className="ph-ic"><ShieldAlert size={23} /></span>
        <div>
          <h1>
            Timesheet Exceptions
            <span className="periodchip">Week {payPeriod.weekNumber} · {periodLabel}</span>
          </h1>
          <div className="ph-sub">
            Answers and comments from this period&apos;s timesheet submissions that matched a configured trigger.
            Each row is one submission and one alert email. The answers are a snapshot, kept even if the timesheet
            is later unlocked and replaced.
          </div>
        </div>
        <div className="ph-right">
          <div className="ph-actions">
            <button type="button" className="tex-btn" disabled={exporting} onClick={doExport}>
              <Download size={15} />{exporting ? 'Building…' : 'Export to Excel'}
            </button>
            <button type="button" className="tex-btn" onClick={() => load(false)}>
              <RefreshCw size={15} />Refresh
            </button>
          </div>
          {updatedAt && (
            <div className="ph-updated">
              updated {updatedAt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kl">Submissions</div>
          <div className={`kv${counts.events === 0 ? ' zero' : ''}`}>{counts.events}</div>
          <div className="ks">{counts.items} exception{counts.items === 1 ? '' : 's'} raised this period</div>
        </div>
        <div className="kpi">
          <div className="kl">Employees</div>
          <div className={`kv${counts.employees === 0 ? ' zero' : ''}`}>{counts.employees}</div>
          <div className="ks">{counts.superseded} replaced submission{counts.superseded === 1 ? '' : 's'}</div>
        </div>
        <div className="kpi">
          <div className="kl">Comments</div>
          <div className={`kv accent${counts.comments === 0 ? ' zero' : ''}`}>{counts.comments}</div>
          <div className="ks">free-text notes from the crew</div>
        </div>
        <div className="kpi">
          <div className="kl">Triggered answers</div>
          <div className={`kv ot${counts.answers === 0 ? ' zero' : ''}`}>{counts.answers}</div>
          <div className="ks">matched a configured trigger</div>
        </div>
      </div>

      {arrivedEventId != null && !arrivalDismissed && (
        <div className={`tex-strip arrival${arrivalMissing ? ' missing' : ''}`}>
          <Mail size={18} />
          <div>
            {arrivalMissing ? (
              <>
                <b>Opened from an alert email, but that submission isn&apos;t here.</b> It may be in a different
                pay period, or outside what you have access to.
              </>
            ) : (
              <>
                <b>Opened from an alert email.</b> Week {payPeriod.weekNumber} is selected and the submission is
                highlighted below.
              </>
            )}
          </div>
          <button type="button" className="strip-act" onClick={() => setArrivalDismissed(true)}>Dismiss</button>
        </div>
      )}

      {unsentCount > 0 && (
        <div className="tex-strip mail">
          <AlertTriangle size={18} />
          <div>
            <b>{unsentCount} alert{unsentCount === 1 ? '' : 's'} didn&apos;t send.</b> Nobody was told about
            {unsentCount === 1 ? ' it' : ' them'} — the exceptions are recorded here, but the email never left.
          </div>
          {firstUnsent && (
            <button type="button" className="strip-act" onClick={() => goToEvent(firstUnsent.timesheetExceptionEventID)}>
              Go to the first one
            </button>
          )}
        </div>
      )}

      {!data.reportActive && data.events.length > 0 && (
        <div className="tex-strip paused">
          <Pause size={18} />
          <div>
            <b>Alert emails are paused.</b> Exceptions below are still being recorded and nothing is lost — only the
            email is stopped.
          </div>
        </div>
      )}

      {counts.withdrawn > 0 && (
        <div className="tex-strip withdrawn">
          <Unlock size={18} />
          <div>
            <b>{counts.withdrawn} withdrawn submission{counts.withdrawn === 1 ? '' : 's'} need
            {counts.withdrawn === 1 ? 's' : ''} attention.</b> Someone reported something, then their timesheet was
            unlocked and never came back — no submitted timesheet exists for that week.
          </div>
          {firstWithdrawn && (
            <button type="button" className="strip-act" onClick={() => goToEvent(firstWithdrawn.timesheetExceptionEventID)}>
              Go to it
            </button>
          )}
        </div>
      )}

      {reviewUnavailable && (
        <div className="tex-strip paused">
          <Pause size={18} />
          <div>{reviewUnavailable}</div>
        </div>
      )}

      {data.events.length > 0 && (
        <div className="tex-filters">
          <FilterBar
            value={query}
            onChange={setQuery}
            placeholder="Search employee, ID, question or answer…"
            label="Filter exceptions"
            matches={visibleEvents.length}
            total={shownTotal}
            active={filtering}
          >
            {reviewsOn && reviewCounts && (
              // Neutral badges on purpose — reviews are optional, so no count here
              // should read as a backlog.
              <div className="seg" role="group" aria-label="Review status">
                {(['all', 'unreviewed', 'reviewed'] as ReviewFilter[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={reviewFilter === f ? 'on' : ''}
                    onClick={() => setReviewFilter(f)}
                  >
                    {f === 'all' ? 'All' : f === 'unreviewed' ? 'Unreviewed' : 'Reviewed'}
                    <span className="segn">
                      {f === 'all' ? counts.events : f === 'unreviewed' ? reviewCounts.unreviewed : reviewCounts.reviewed}
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="seg">
              {(['all', 'comments', 'answers'] as ItemKind[]).map((k) => (
                <button
                  key={k}
                  type="button"
                  className={kind === k ? 'on' : ''}
                  onClick={() => setKind(k)}
                >
                  {k === 'all' ? 'All' : k === 'comments' ? 'Comments' : 'Answers'}
                  <span className="segn">{k === 'all' ? counts.items : k === 'comments' ? counts.comments : counts.answers}</span>
                </button>
              ))}
            </div>
            <label
              className={`replaced${reviewFilter === 'unreviewed' ? ' off' : ''}`}
              title={reviewFilter === 'unreviewed' ? 'Replaced submissions are already left out of Unreviewed.' : undefined}
            >
              <input
                type="checkbox"
                checked={hideReplaced}
                disabled={reviewFilter === 'unreviewed'}
                onChange={() => setHideReplaced((v) => !v)}
              />
              Hide replaced submissions
              {counts.superseded > 0 && <span className="repn">{counts.superseded}</span>}
            </label>
          </FilterBar>

          <div className="tex-result">
            <b>{visibleEvents.length}</b> of <b>{shownTotal}</b>
            {' '}submission{visibleEvents.length === 1 ? '' : 's'} · <b>{visibleItemCount}</b> exception
            {visibleItemCount === 1 ? '' : 's'}
            {filtering && <span className="filtered">filtered</span>}
            {hiddenReplaced > 0 && <span className="hidden-note"> · {hiddenReplaced} replaced submission{hiddenReplaced === 1 ? '' : 's'} hidden</span>}
          </div>
        </div>
      )}

      {emptyState}

      {filterEmpty === 'unreviewed' && (
        <div className="tex-empty">
          <span className="ei neutral"><CheckCircle2 size={20} /></span>
          <div><span className="e-title">No unreviewed submissions.</span></div>
          <button type="button" className="strip-act" onClick={() => setReviewFilter('all')}>Show all</button>
        </div>
      )}

      {filterEmpty === 'reviewed' && (
        <div className="tex-empty">
          <span className="ei neutral"><CheckCircle2 size={20} /></span>
          <div><span className="e-title">No reviewed submissions yet.</span></div>
          <button type="button" className="strip-act" onClick={() => setReviewFilter('all')}>Show all</button>
        </div>
      )}

      {filterEmpty === 'filter' && (
        <div className="tex-empty">
          <span className="ei neutral"><Search size={20} /></span>
          <div>
            <span className="e-title">Nothing matches your filter.</span><br />
            There {counts.events === 1 ? 'is' : 'are'} {counts.events} submission{counts.events === 1 ? '' : 's'} with
            exceptions this week{term ? <>, but none match &ldquo;{query.trim()}&rdquo;</> : ''}.
          </div>
          <button
            type="button"
            className="strip-act"
            onClick={() => { setQuery(''); setKind('all'); setReviewFilter('all'); }}
          >
            Clear filters
          </button>
        </div>
      )}

      <div className="tex-list">{visibleEvents.map(eventCard)}</div>

      {popupEvent && reviewPopup && (
        <ReviewPopup
          // Remount per card/mode so the textarea starts from that card's note.
          key={`${reviewPopup.eventID}-${reviewPopup.mode}`}
          event={popupEvent}
          mode={reviewPopup.mode}
          onClose={() => setReviewPopup(null)}
          onSubmit={(id, action, note) => runReview(id, action, note)}
        />
      )}

    </div>
  );
};

export default TimesheetExceptionsPage;
